import { BaseItem, NullablePartial } from './common';

export enum Kind {
  EhrPush = 'EHR_PUSH',
  SyncList = 'SYNC_LIST',
}

export enum Level {
  Error = 'error',
  Warn = 'warn',
  Info = 'info',
  Verbose = 'verbose',
  Debug = 'debug',
  Silly = 'silly',
}

export enum ListJobKind {
  AppointmentUpdateAggregation = 'APPOINTMENT_UPDATE_AGGREGATION',
  CalendarSync = 'CALENDAR_SYNC',
  Outreach = 'OUTREACH',
  PatientList = 'PATIENT_LIST',
  PatientOrders = 'PATIENT_ORDERS',
}

export type SyncJob = ListJobKind;

export enum ReportType {
  Confirmation = 'CONFIRMATION',
  MammoScheduling = 'MAMMO_SCHEDULING',
  Scheduling = 'SCHEDULING',
}

export const DayOfWeek = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const;
export type DayOfWeek = (typeof DayOfWeek)[keyof typeof DayOfWeek];

export const Hour = {
  '12 AM': 0,
  '01 AM': 1,
  '02 AM': 2,
  '03 AM': 3,
  '04 AM': 4,
  '05 AM': 5,
  '06 AM': 6,
  '07 AM': 7,
  '08 AM': 8,
  '09 AM': 9,
  '10 AM': 10,
  '11 AM': 11,
  '12 PM': 12,
  '01 PM': 13,
  '02 PM': 14,
  '03 PM': 15,
  '04 PM': 16,
  '05 PM': 17,
  '06 PM': 18,
  '07 PM': 19,
  '08 PM': 20,
  '09 PM': 21,
  '10 PM': 22,
  '11 PM': 23,
} as const;
export type Hour = (typeof Hour)[keyof typeof Hour];

export const Interval = {
  'Every minute': 1,
  'Every 2 minutes': 2,
  'Every 5 minutes': 5,
  'Every 10 minutes': 10,
  'Every 15 minutes': 15,
  'Every half hour': 30,
  'Every hour': 60,
  'Every 2 hours': 120,
  'Every 6 hours': 360,
  'Every 12 hours': 720,
  'Every day': 1440,
} as const;
export type Interval = (typeof Interval)[keyof typeof Interval];

export enum Ordinality {
  First = 1,
  Second = 2,
  Third = 3,
  Fourth = 4,
  Fifth = 5,
}

export type OrdinalDay = { day: DayOfWeek; ordinality?: Ordinality };

export interface PollingSchedule {
  firstHour: Hour;
  lastHour: Hour;
  intervalMinutes: Interval;
  daysOfWeek: OrdinalDay[];
}

export interface SFTPParams {
  filepath: string;
  useNewServer?: boolean;
}

type JobConfig<T extends object | void = void> = {
  pollingSchedule: PollingSchedule;
  sftpParams?: SFTPParams;
} & (T extends void ? {} : { jobParams: T });

export interface CalendarSyncJobParams {
  firstRelativeDate: number;
  lastRelativeDate: number;
  reportType: ReportType;
  useWorkerReplicaDb?: boolean;
}

export interface OutreachSyncJobParams {
  outreachKind: string;
}

export type AppointmentUpdateAggregationConfig = JobConfig;
export type CalendarSyncConfig = JobConfig<CalendarSyncJobParams>;
export type OutreachSyncConfig = JobConfig<OutreachSyncJobParams>;
export type PatientListSyncConfig = JobConfig;
export type PatientOrdersSyncConfig = JobConfig;
export type ListJobConfig =
  | AppointmentUpdateAggregationConfig
  | CalendarSyncConfig
  | OutreachSyncConfig
  | PatientListSyncConfig
  | PatientOrdersSyncConfig
  | null;

export interface BaseFeed {
  organizationId: string | null;
  kind: Kind;
  name: string;
  enabled: boolean;
  itemTrackingEnabled: boolean;
  practiceIds: string[];
  listJobKind: ListJobKind | null;
  listJobConfig: ListJobConfig;
  deletedTimestamp: Date | null;
  ehrInstanceId: string | null;
}

export interface Feed extends BaseFeed, BaseItem {}

export type CreateValues = NullablePartial<Omit<BaseFeed, 'deletedTimestamp'>>;

export type UpdateValues = Partial<BaseFeed>;

export type ImportValidation = { value: string; error?: string };

export interface Upload extends BaseItem {
  csvGsPath: string;
  feedId: string;
  userId: string | null;
  status: string | null;
}

export interface UploadLog extends BaseItem {
  feedUploadId: string;
  log: string;
  level: Level;
}

export interface FindAllOptions
  extends Partial<
    Pick<BaseFeed, 'kind' | 'enabled' | 'itemTrackingEnabled' | 'listJobKind'>
  > {
  q?: string;
  practiceId?: string;
  includeDeleted?: boolean;
}

export enum Operation {
  Date = 'DATE',
  Datetime = 'DATETIME',
  Language = 'LANGUAGE',
  AddLeadingZeroes = 'ADD_LEADING_ZEROES',
  Lowercase = 'LOWERCASE',
  Pascalcase = 'PASCALCASE',
  Sex = 'SEX',
  Uppercase = 'UPPERCASE',
  EpicDiagnosis = 'EPIC_DIAGNOSIS',
}

export type Transform =
  | { operation: Operation.Date; format: string }
  | { operation: Operation.Datetime; format: string }
  | { operation: Operation.Language }
  | { operation: Operation.AddLeadingZeroes; totalCharacters: number }
  | { operation: Operation.Lowercase }
  | { operation: Operation.Pascalcase }
  | { operation: Operation.Sex }
  | { operation: Operation.Uppercase }
  | { operation: Operation.EpicDiagnosis };

export type FieldMapping = NullablePartial<{
  field: string;
  dataBinding: string;
  transform: Transform | null;
}>;

export function isAppointmentUpdateAggregationFeed<
  T extends Pick<Feed, 'listJobKind' | 'listJobConfig'>,
>(
  feed: T,
): feed is T & {
  listJobKind: ListJobKind.AppointmentUpdateAggregation;
  listJobConfig: AppointmentUpdateAggregationConfig;
} {
  return feed.listJobKind === ListJobKind.AppointmentUpdateAggregation;
}

export function isCalendarSyncFeed<
  T extends Pick<Feed, 'listJobKind' | 'listJobConfig'>,
>(
  feed: T,
): feed is T & {
  listJobKind: ListJobKind.CalendarSync;
  listJobConfig: CalendarSyncConfig;
} {
  return feed.listJobKind === ListJobKind.CalendarSync;
}

export function isOutreachSyncFeed<
  T extends Pick<Feed, 'listJobKind' | 'listJobConfig'>,
>(
  feed: T,
): feed is T & {
  listJobKind: ListJobKind.Outreach;
  listJobConfig: OutreachSyncConfig;
} {
  return feed.listJobKind === ListJobKind.Outreach;
}

export function isPatientListSyncFeed<
  T extends Pick<Feed, 'listJobKind' | 'listJobConfig'>,
>(
  feed: T,
): feed is T & {
  listJobKind: ListJobKind.PatientList;
  listJobConfig: PatientListSyncConfig;
} {
  return feed.listJobKind === ListJobKind.PatientList;
}

export function isPatientOrdersSyncFeed<
  T extends Pick<Feed, 'listJobKind' | 'listJobConfig'>,
>(
  feed: T,
): feed is T & {
  listJobKind: ListJobKind.PatientOrders;
  listJobConfig: PatientOrdersSyncConfig;
} {
  return feed.listJobKind === ListJobKind.PatientOrders;
}
